import { AlertTriangle, CheckCircle, DollarSign, Shield, Users, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { romaneioService } from '../../api';
import { NoOpsSelected, PageLoading, RefreshButton } from '../../components';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../auth/AuthContext';
import { useOps } from '../../hooks/useOps';
import { useAppSelector } from '../../store';
import { formatCurrencyBrlFromCents, formatDateBR } from '../../utils';
import { OperationConfirmDialog } from '../../components/ui/operation-confirm-dialog';

interface CheckoutRow {
  checkout_date: string;
  entregadores_unicos: number;
  total_valor_face: number;
  ticket_medio: number;
  valor_total_garantido?: number;
  entregadores_no_match?: number;
  ops: string;
}

interface ExtraCardDef {
  label: string;
  getValue: (row: CheckoutRow) => string | number;
  bg: string;
  border: string;
  textColor: string;
  icon: React.ReactElement;
}

const garantidoCard: ExtraCardDef = {
  label: 'Valor Total Garantido',
  getValue: (row) => formatCurrencyBrlFromCents(Number(row.valor_total_garantido) || 0),
  bg: 'bg-[hsl(270,50%,97%)]',
  border: 'border-[hsl(270,50%,85%)]',
  textColor: 'text-[hsl(270,50%,40%)]',
  icon: <Shield className="h-7 w-7 text-[hsl(270,50%,50%)]" />,
};

const noMatchCard: ExtraCardDef = {
  label: 'Entregadores s/ Match',
  getValue: (row) => Number(row.entregadores_no_match) || 0,
  bg: 'bg-[hsl(43,56%,97%)]',
  border: 'border-[hsl(43,56%,80%)]',
  textColor: 'text-[hsl(43,56%,35%)]',
  icon: <AlertTriangle className="h-7 w-7 text-[hsl(43,56%,61%)]" />,
};

const OPS_EXTRA_CARDS: Record<string, ExtraCardDef[]> = {
  muve: [garantidoCard, noMatchCard],
  xpto: [garantidoCard, noMatchCard],
  logus: [garantidoCard],
};

function getExtraCards(ops: string): ExtraCardDef[] {
  return OPS_EXTRA_CARDS[ops.toLowerCase()] ?? [];
}

function getIndividualsLabel(ops: string): string {
  if (ops.toLowerCase().includes('indrive')) return 'Motoristas Únicos';
  return 'MB Único';
}

export function RomaneioCheckout() {
  const { currentOps, getCurrentOpsConfig } = useOps();
  const { hasPermission } = useAuth();
  // Gates the "Finalizar Checkout" action (backend enforces too).
  const canSubmitCheckout = hasPermission('romaneioCheckoutSubmmit', currentOps || undefined);
  const { opsConfigs } = useAppSelector((state) => state.ops);
  const user = useAppSelector((state) => state.auth.user);
  const [data, setData] = useState<CheckoutRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<{ date: string; msg: string } | null>(null);
  const [confirmRow, setConfirmRow] = useState<CheckoutRow | null>(null);

  const getOpsConfig = () => {
    const opsConfig = getCurrentOpsConfig();
    return { dbName: opsConfig?.dbName };
  };

  const loadData = async () => {
    if (!currentOps) return;
    const { dbName } = getOpsConfig();
    if (!dbName) return;

    setLoading(true);
    setError('');
    setSubmitSuccess(null);

    try {
      const response = await romaneioService.getCheckoutSummary({ dbName, ops: currentOps });
      setData(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados de checkout');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (raw: unknown): string => formatDateBR(String(raw ?? ''));

  const handleConfirmCheckout = async (row: CheckoutRow) => {
    if (!currentOps) return;
    const { dbName } = getOpsConfig();
    if (!dbName) return;

    setSubmitting(row.checkout_date);
    setSubmitError(null);
    setError('');

    try {
      await romaneioService.submitCheckout({
        dbName,
        ops: currentOps,
        checkoutDate: row.checkout_date,
        userInfo: { email: user?.email || '' },
      });
      setSubmitSuccess(row.checkout_date);
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Erro ao finalizar checkout';
      setSubmitError({ date: row.checkout_date, msg });
    } finally {
      setSubmitting(null);
    }
  };

  useEffect(() => {
    if (!currentOps) return;
    const { dbName } = getOpsConfig();
    if (!dbName) return;

    const abortController = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await romaneioService.getCheckoutSummary({ dbName, ops: currentOps });
        if (abortController.signal.aborted || !isMounted) return;
        setData(response.data || []);
      } catch (err: any) {
        if (abortController.signal.aborted || !isMounted) return;
        setError(err.message || 'Erro ao carregar dados de checkout');
      } finally {
        if (!abortController.signal.aborted && isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [currentOps, opsConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentOps) {
    return (
      <NoOpsSelected message="Selecione uma operação para visualizar os dados de checkout." />
    );
  }

  const extraCards = getExtraCards(currentOps);
  const individualsLabel = getIndividualsLabel(currentOps);
  const totalCols = 2 + extraCards.length;
  const gridClass =
    totalCols <= 2
      ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
      : totalCols === 3
        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[hsl(210,50%,10%)]">
            Checkout
          </h1>
          <p className="text-[hsl(215,16%,47%)]">
            Resumo de checkout — {currentOps}
          </p>
        </div>
        <RefreshButton onClick={loadData} loading={loading} />
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <XCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      <PageLoading loading={loading} message="Carregando dados de checkout..." />

      {!loading && data.length === 0 && (
        <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
          <CardContent className="text-center p-8">
            <h3 className="text-lg font-medium text-[hsl(210,50%,10%)] mb-2">
              Nenhum checkout pendente
            </h3>
            <p className="text-sm text-[hsl(215,16%,47%)]">
              Todos os romaneios já foram processados.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && data.length > 0 && (
        <div className="space-y-6">
          {data.map((row) => (
            <Card
              key={row.checkout_date}
              className="bg-white border-[hsl(214,32%,91%)] shadow-sm"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-xs text-[hsl(215,16%,47%)] mb-0.5">Data do romaneio</p>
                    <h2 className="text-xl font-semibold text-[hsl(210,50%,10%)]">
                      {formatDate(row.checkout_date)}
                    </h2>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {submitSuccess === row.checkout_date ? (
                      <Badge className="bg-[hsl(142,76%,36%)] text-white text-sm px-3 py-1.5">
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5 inline" />
                        Checkout solicitado
                      </Badge>
                    ) : (
                      <Button
                        onClick={() => setConfirmRow(row)}
                        disabled={submitting === row.checkout_date || !canSubmitCheckout}
                        className="bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white"
                      >
                        {submitting === row.checkout_date ? 'Finalizando...' : 'Finalizar Checkout'}
                      </Button>
                    )}
                    {submitError?.date === row.checkout_date && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <XCircle className="h-3.5 w-3.5 shrink-0" />
                        {submitError.msg}
                      </p>
                    )}
                  </div>
                </div>

                <div className={gridClass}>
                  {/* Base card: individuals */}
                  <Card className="bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-[hsl(215,16%,47%)] mb-1">
                            {individualsLabel}
                          </p>
                          <p className="text-2xl font-bold text-[hsl(210,35%,32%)]">
                            {Number(row.entregadores_unicos) || 0}
                          </p>
                        </div>
                        <Users className="h-7 w-7 text-[hsl(210,35%,32%)]" />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Base card: face value */}
                  <Card className="bg-[hsl(142,76%,97%)] border-[hsl(142,76%,85%)]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-[hsl(215,16%,47%)] mb-1">
                            Valor de Face
                          </p>
                          <p className="text-2xl font-bold text-[hsl(142,76%,30%)]">
                            {formatCurrencyBrlFromCents(Number(row.total_valor_face) || 0)}
                          </p>
                        </div>
                        <DollarSign className="h-7 w-7 text-[hsl(142,76%,36%)]" />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Ops-specific extra cards */}
                  {extraCards.map((card) => (
                    <Card key={card.label} className={`${card.bg} ${card.border}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-[hsl(215,16%,47%)] mb-1">
                              {card.label}
                            </p>
                            <p className={`text-2xl font-bold ${card.textColor}`}>
                              {card.getValue(row)}
                            </p>
                          </div>
                          {card.icon}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <OperationConfirmDialog
        open={confirmRow !== null}
        onOpenChange={(open) => !open && setConfirmRow(null)}
        onConfirm={() => {
          if (confirmRow) handleConfirmCheckout(confirmRow);
        }}
        action="checkout"
        ops={currentOps}
        date={confirmRow?.checkout_date}
      />
    </div>
  );
}
